import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import {UserRoleEnum} from '@/enums/userRolesEnum'
import {BallPulseLoader} from 'vue-loaders';
import TitleDropdownPicker from "@/componentsReusable/TitleDropdownPicker.vue";
import FacilitiesAdvancedSearchFields from "@/components/facilities/FacilitiesAdvancedSearchFields";
import ProviderAdvancedSearchFields from "@/components/providers/ProviderAdvancedSearchFields";
import {createAdvancedSearchFilters} from "@/components/providers/models/ProviderAdvancedSearchModel";
import {createFacilitySearchFields} from "@/components/facilities/models/FacilityAdvancedSearchModel";
import {HTTP} from "@/utils/httpCommonUtil";
import {ViewStateType} from "@/enums/ViewStateType";
import VueSimpleSpinner from "vue-simple-spinner";
import {ContactModel, ContactType} from "@/components/communications/models/contactModel";
import NewCampaignResultsDisplay from "@/components/communications/campaign/results/NewCampaignResultsDisplay";
import CampaignMessageCard from "@/components/communications/campaign/CampaignMessageCard/CampaignMessageCard";
import GenericModal from "@/componentsReusable/genericModal.vue";
import CampaignGroupCard from "@/components/communications/campaign/CampaignGroupCard/CampaignGroupCard";

@Component({
  components: {
    BallPulseLoader,
    TitleDropdownPicker,
    FacilitiesAdvancedSearchFields,
    ProviderAdvancedSearchFields,
    VueSimpleSpinner,
    NewCampaignResultsDisplay,
    CampaignMessageCard,
    GenericModal,
    CampaignGroupCard,
  },
})
export default class CommunicationsCampaign extends Vue {

  @Prop({type: String, required: true, default: ''})
  messageTypeText!: string;
  @Prop({type: String, required: true, default: ''})
  recipientTypeId!: string;

  protected isAuthorized = false;
  protected searchResults: ContactModel[] = [];
  protected selectedContacts: ContactModel[] = [];
  protected originalGroupIds = [] as number[];
  protected searchParametersCardExpanded = true;
  protected loadingSearchResults = false;
  protected messageOptions = [{text: "Texts", id: 1, bulletClass: ""}, {text: "Emails", id: 2, bulletClass: ""}];
  protected recipientOptions = [{
    text: "Provider",
    id: ContactType.Provider,
    bulletClass: "nav-circle background-blue"
  }, {text: "Facility Contacts", id: ContactType.FacilityContact, bulletClass: "nav-circle background-red"}];
  protected selectedMessageType = {};
  protected confirmedMessageType = {};
  protected selectedRecipientType = {text: "Provider", id: 1};
  protected simpleSearchValue = '';
  protected advancedSearchFilters = createAdvancedSearchFilters();
  protected advancedFacilitySearchFilters = createFacilitySearchFields();
  protected showRestartConfirmationModal = false;
  protected oldMessageType = {};
  protected disableContact = false;
  protected contactGroupUploaded = false;
  protected enableGroupUpdate = false;
  protected sameGroup = false;
  protected loadedGroupId = 0;
  protected groupName = '';

  get showFacilitySearch(): boolean {
    return this.selectedRecipientType.id === 2;
  }

  get filteredSearchResults(): ContactModel[] {
    return this.searchResults.filter(contact =>
      this.selectedContacts.filter(sel => sel.id === contact.id).length === 0
    );
  }

  @Watch('selectedMessageType')
  messageTypeTextChanged(newMessageType: any, oldMessageType: any) {
    if (newMessageType !== this.confirmedMessageType) {
      this.showRestartConfirmationModal = true;
    }
  }

  @Watch('selectedContacts')
  loadedGroupChanged() {
    this.sameGroup = this.compareGroups(this.originalGroupIds, this.selectedContacts);
    this.enableGroupUpdate = this.sameGroup;
  }

  compareGroups(loadedGroupIds: number[], newGroup: ContactModel[]) {
    if (loadedGroupIds.length !== newGroup.length) {
      return false
    }
    loadedGroupIds = loadedGroupIds.sort();
    const newGroupIds = newGroup.map((contact: ContactModel) => contact.id).sort();
    for (let i = 0; i < newGroupIds.length; i++) {
      if (loadedGroupIds[i] !== newGroupIds[i]) {
        return false;
      }
    }
    return true;
  }

  async simpleSearch() {
    if (this.selectedRecipientType.id === 1) {
      try {
        await this.$store.dispatch('viewState/updateViewState', {
          name: ViewStateType.PROVIDER_CONTACT_SEARCH_SIMPLE,
          value: this.simpleSearchValue
        });

        const response = await HTTP.get('/api/providers/search?providerNameLike=' + this.simpleSearchValue + '&forceResults=true&excludeInactive=true');
        const contacts = this.setResultType(response.data.data.results);
        this.searchResults = this.filterContactByCommunicationType((this.confirmedMessageType as any).id, contacts)
      } catch (ex) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error Getting Results',
          text: 'Something went wrong getting search results!',
        });
      } finally {
        this.loadingSearchResults = false;
      }
    } else {
      this.advancedFacilitySearchFilters = createFacilitySearchFields();
      this.advancedFacilitySearchFilters.nameFilter = this.simpleSearchValue;
      await this.searchFacilities();
    }
  }

  filterContactByCommunicationType(messageTypeId: number, contacts: ContactModel[]) {
    if ((this.confirmedMessageType as any).id === 1) {
      return this.filterContactsWithSMS(contacts);
    } else {
      return this.filterContactsWithEmail(contacts);
    }
  }

  filterContactsWithEmail(contacts: ContactModel[]) {
    return contacts.filter((contact: ContactModel) => contact.email && contact.email.length > 0);
  }

  filterContactsWithSMS(contacts: ContactModel[]) {
    return contacts.filter((contact: ContactModel) => (contact.phoneCell && contact.phoneCell.length > 0));
  }

  setResultType(contacts: ContactModel[]) {
    for (let i = 0; i < contacts.length; i++) {
      contacts[i].contactType = this.selectedRecipientType.id;
    }
    return contacts;
  }

  async searchProviders() {
    let forceResults = true;
    if (this.loadingSearchResults) {
      return;
    }
    this.loadingSearchResults = true;
    await this.$store.dispatch('viewState/updateViewState', {
      name: ViewStateType.PROVIDER_CONTACT_SEARCH_ADVANCED,
      value: JSON.parse(JSON.stringify(this.advancedSearchFilters))
    });
    try {
      const response = await HTTP.post('/api/providers/advanced-search?forceResults=' + forceResults, {advancedSearchFilters: this.advancedSearchFilters});
      this.searchResults = this.setResultType(response.data.data.results);
      this.searchResults = this.filterContactByCommunicationType((this.confirmedMessageType as any).id, this.searchResults);
    } catch (ex) {
      this.$notify({
        group: 'main',
        type: 'error',
        title: 'Error Getting Results',
        text: 'Something went wrong getting search results!',
      });
    } finally {
      this.loadingSearchResults = false;
    }
  }

  async clearAllFacility() {
    this.advancedFacilitySearchFilters = createFacilitySearchFields();
  }

  async clearAll() {
    this.advancedSearchFilters = createAdvancedSearchFilters();
  }

  async searchFacilities() {
    let forceResults = true;
    if (this.loadingSearchResults) {
      return;
    }
    this.loadingSearchResults = true;
    await this.$store.dispatch('viewState/updateViewState', {
      name: ViewStateType.FACILITY_SEARCH_ADVANCED,
      value: JSON.parse(JSON.stringify(this.advancedFacilitySearchFilters))
    });
    try {
      const response = await HTTP.post('/api/facilities/contact/advanced-search?forceResults=' + forceResults
        , {advancedSearchFilters: this.advancedFacilitySearchFilters});
      const resultIds = new Set();
      this.searchResults = [];
      response.data.data.forEach((d: ContactModel) => {
        d.contactType = ContactType.FacilityContact;
        if (!resultIds.has(d.id)) {
          resultIds.add(d.id);
          this.searchResults.push(d);
        }
      });
      this.searchResults = this.filterContactByCommunicationType((this.confirmedMessageType as any).id, this.searchResults)
      this.loadingSearchResults = false;
    } catch (ex) {
      this.$notify({
        group: 'main',
        type: 'error',
        title: 'Error Getting Results',
        text: 'Something went wrong getting search results!',
      });
    } finally {
      this.loadingSearchResults = false;
    }
  }

  created() {
    this.isAuthorized = this.$store.getters['user/hasRole']([UserRoleEnum.MANAGER, UserRoleEnum.COMMUNICATIONS]);

    this.selectedMessageType = this.messageOptions.filter(value => value.text === this.messageTypeText)[0] || this.messageOptions[0];
    this.selectedRecipientType = this.recipientOptions.filter(value => value.id.toString() === this.recipientTypeId)[0] || this.recipientOptions[0];
    this.advancedFacilitySearchFilters = this.$store.state.viewState[ViewStateType.FACILITY_SEARCH_ADVANCED] || createFacilitySearchFields();

    this.advancedSearchFilters = this.$store.state.viewState[ViewStateType.PROVIDER_CONTACT_SEARCH_ADVANCED] || createAdvancedSearchFilters();
    this.selectedContacts = this.$store.state.viewState[ViewStateType.SELECTED_CAMPAIGN_CONTACTS] || [];
    this.confirmedMessageType = this.selectedMessageType;
  }

  async searchClicked(contact: ContactModel) {
    if (this.isDisabled(contact)) {
      return;
    }
    this.selectedContacts.push(contact);
    await this.storeSelectedContacts();
  }

  isDisabled(contact: ContactModel): boolean {
    return ((this.confirmedMessageType as any).id === 1 && !contact.receiveSMSBroadcast) || ((this.confirmedMessageType as any).id === 2 && !contact.receiveEmailBroadcast);
  }

  async unselectClicked(contact: ContactModel) {
    const index = this.selectedContacts.indexOf(contact);
    if (index === -1) {
      return
    }
    this.selectedContacts.splice(index, 1);
    if (this.selectedContacts.length === 0) {
      this.contactGroupUploaded = false;
    }
    await this.storeSelectedContacts();
  }

  async removeAll() {
    this.selectedContacts = [];
    await this.storeSelectedContacts();
  }

  async addAll() {
    const toAdd = this.filteredSearchResults.filter(contact => !this.isDisabled(contact)).map(contact => ({ ...contact }));
    this.selectedContacts.push(...toAdd);
    await this.storeSelectedContacts();
  }

  async storeSelectedContacts(): Promise<void> {
    await this.$store.dispatch('viewState/updateViewState', {
      name: ViewStateType.SELECTED_CAMPAIGN_CONTACTS,
      value: JSON.parse(JSON.stringify(this.selectedContacts))
    });
  }

  async startOver() {
    await this.reset();
    const input = this.$refs.simpleSearchInput as HTMLInputElement;
    input.focus();
  }

  async resetCanceled() {
    this.selectedMessageType = this.confirmedMessageType;
    this.showRestartConfirmationModal = false;
  }

  async resetMessage() {
    this.confirmedMessageType = this.selectedMessageType;
    this.showRestartConfirmationModal = false;
  }

  async reset() {
    await this.removeAll();
    await this.storeSelectedContacts();
    this.searchResults = [];

    this.simpleSearchValue = '';
    await this.$store.dispatch('viewState/updateViewState', {
      name: ViewStateType.PROVIDER_CONTACT_SEARCH_SIMPLE,
      value: this.simpleSearchValue
    });

    this.advancedSearchFilters = createAdvancedSearchFilters();
    await this.$store.dispatch('viewState/updateViewState', {
      name: ViewStateType.PROVIDER_CONTACT_SEARCH_ADVANCED,
      value: JSON.parse(JSON.stringify(this.advancedSearchFilters))
    });

    this.advancedFacilitySearchFilters = createFacilitySearchFields();
    await this.$store.dispatch('viewState/updateViewState', {
      name: ViewStateType.FACILITY_SEARCH_ADVANCED,
      value: JSON.parse(JSON.stringify(this.advancedFacilitySearchFilters))
    });
  }

  async cancelCampaignClicked() {
    await this.reset();
    this.$router.back();
  }

  async updateOriginalGroup(contacts: ContactModel[]) {
    this.originalGroupIds = contacts.map((contact: ContactModel) => contact.id);
    this.sameGroup = this.compareGroups(this.originalGroupIds, this.selectedContacts);
  }

  async setGroupContacts(contacts: ContactModel[], loadedGroup: any) {
    this.setResultType(contacts);
    this.loadedGroupId = loadedGroup.id;
    this.groupName = loadedGroup.groupName;
    this.contactGroupUploaded = true;
    this.searchResults = contacts;
    this.selectedContacts = contacts.filter((contact:ContactModel) => !this.isDisabled(contact));
    this.originalGroupIds = contacts.map((contact: ContactModel) => contact.id);
  }
}



// WEBPACK FOOTER //
// ./src/components/communications/campaign/CommunicationsCampaign.ts